import { useEffect, useState, useMemo, useCallback } from "react";
import JobFilters from "./JobFilters";
import JobList from "./JobList";
import Pagination from "./Pagination";
import { fetchAllJobs } from "../../apis/JobApis";
import { useToastHelpers } from "../Toasts/ToastUtils";
import SeoMeta from "../SeoMeta";
import ReferralsSlider from "./ReferralSlider";
import { CapgeminiReferral, InfosysSpringboardPragati } from "./SideComponents";
import AuthPromptModal from "../Authentication/AuthPromptModal";
import { useAuth } from "../Authentication/AuthContext";
import CoursePromoAd from "../courses/CoursePromoAd";
import { SkeletonJobList } from "../Skeletons";
import FloatingWhatsappButton from "../FloatingWhatsappButton";
import { useTheme } from "../Theme/ThemeContext";
import { useNotifications } from "../notifications/NotificationContext";

export default function JobsPage({ type }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [activeItems, setActiveItems] = useState(0);
  const ITEMS_PER_PAGE = 10;
  const formattedType = type.charAt(0).toUpperCase() + type.slice(1);

  const [keyword, setKeyword] = useState("");
  const { successToast } = useToastHelpers();
  const [selectedExperience, setSelectedExperience] = useState("All");
  const [selectedLocation, setSelectedLocation] = useState("All");

  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const { clearNewJobsCount } = useNotifications();
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Clear notification count when viewing jobs page
  useEffect(() => {
    clearNewJobsCount();
  }, [clearNewJobsCount]);

  useEffect(() => {
    if (!user) {
      setShowLoginModal(true);
    }
  }, [user]);

  // Extract unique locations for filters
  const locations = useMemo(() => {
    const uniqueLocations = new Set(["All"]);
    jobs.forEach((job) => {
      if (Array.isArray(job.locations)) {
        job.locations.forEach((loc) => {
          if (loc && loc.trim() !== "") uniqueLocations.add(loc.trim());
        });
      }
    });
    return Array.from(uniqueLocations);
  }, [jobs]);

  // Calculate total pages based on type
  // For jobs, show only active jobs (they expire)
  // For internships and referrals, show all (they usually don't expire)
  const totalPages = useMemo(() => {
    const itemsToShow = type === "job" ? activeItems : totalItems;
    return Math.ceil(itemsToShow / ITEMS_PER_PAGE);
  }, [type, activeItems, totalItems]);

  // Fetch jobs
  const loadJobs = useCallback(async (newPage = 1) => {
    setLoading(true);

    const params = {
      page: newPage,
      size: ITEMS_PER_PAGE,
      type: type,
      // For jobs, only show active ones; for internships/referrals show all
      status: type === "job" ? "active" : undefined,
      location: selectedLocation !== "All" ? selectedLocation : "",
      experience: selectedExperience !== "All" ? selectedExperience : "",
      keyword: keyword || "",
    };

    const result = await fetchAllJobs(params);

    if (result) {
      const jobsList = Array.isArray(result?.jobs) ? result.jobs : [];
      setJobs(jobsList);
      setTotalItems(result?.total || 0);
      setActiveItems(result?.active_jobs || 0);
      setPage(newPage);

      // Determine if last page based on API response
      const itemsToShow = type === "job" ? (result?.active_jobs || 0) : (result?.total || 0);
      const calculatedTotalPages = Math.ceil(itemsToShow / ITEMS_PER_PAGE);
      setIsLastPage(newPage >= calculatedTotalPages || jobsList.length === 0);
    } else {
      successToast("No more/matching jobs found");
      setIsLastPage(true);
    }

    setLoading(false);
  }, [keyword, selectedExperience, selectedLocation, type, successToast]);

  useEffect(() => {
    loadJobs(1);
  }, [loadJobs]);

  return (
    <>
      <SeoMeta
        title={`Latest ${formattedType + "s "}${selectedLocation !== "All" ? " in " + selectedLocation : ""
          } 2025 | Freshers IT Jobs India | Code Arena`}
        description={`Explore fresh ${formattedType + "s "
          }openings${selectedLocation !== "All" ? " in " + selectedLocation : " across India"
          }. Find fresher IT jobs, off-campus placements, internships & referrals at top companies. Apply now on Code Arena!`}
        url={`https://www.code-arena.in/${type}s`}
        image="/logo192.png"
        keywords={`${formattedType}s, freshers jobs India, IT jobs 2025, off-campus placements, internships, job referrals, software developer jobs, tech jobs freshers, entry level jobs India`}
      />

      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white dark:from-neutral-900 dark:to-neutral-800">
        {/* Hero Section */}
        <div className="text-white" style={{ background: `linear-gradient(135deg, rgb(var(--color-primary)) 0%, rgb(var(--color-secondary)) 50%, rgb(var(--color-accent)) 100%)` }}>
          <div className="ca-container py-10 sm:py-14">
            <div className="max-w-2xl">
              <h1 className="text-3xl sm:text-4xl font-bold mb-3">
                {formattedType === "Job" ? "Latest Jobs" : `${formattedType}s`}
              </h1>
              <p className="text-white/80 text-lg">
                Discover your next career opportunity. Browse {formattedType.toLowerCase()}s from top companies.
              </p>
            </div>
          </div>
        </div>

        <div className="ca-container py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters */}
            <div className="w-full lg:w-1/4">
              <div className="sticky top-4">
                <JobFilters
                  locations={locations}
                  keyword={keyword}
                  setKeyword={setKeyword}
                  selectedExperience={selectedExperience}
                  setSelectedExperience={setSelectedExperience}
                  selectedLocation={selectedLocation}
                  setSelectedLocation={setSelectedLocation}
                />
              </div>
            </div>

            {/* Jobs List */}
            <div className="w-full lg:w-2/4 flex flex-col">
              {loading ? (
                <SkeletonJobList count={5} />
              ) : (
                <div className="md:max-h-[200vh] md:overflow-y-auto md:overflow-x-hidden hide-scrollbar">
                  <JobList
                    jobs={jobs}
                    currentPage={page}
                    setCurrentPage={loadJobs}
                    isLastPage={isLastPage}
                  />
                </div>
              )}

              {/* Pagination */}
              <div className="mt-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={type === "job" ? activeItems : totalItems}
                  itemsPerPage={ITEMS_PER_PAGE}
                  isLastPage={isLastPage}
                  onPageChange={loadJobs}
                  showInfo={true}
                />
              </div>
            </div>

            <FloatingWhatsappButton />

            {/* Ads */}
            <div className="w-full lg:w-1/4 space-y-6">
              <CoursePromoAd variant="sidebar" />
              <CapgeminiReferral />
              <InfosysSpringboardPragati />
              {/* <GoogleAd /> */}
              {/* <AmpAd /> */}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AuthPromptModal
        show={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
      {/* <PreferenceModal
        user={user}
        userDetails={loggedInUser}
        show={showPrefModal}
        onComplete={() => setShowPrefModal(false)}
      /> */}
      <ReferralsSlider />
    </>
  );
}
